import { Table, Pagination, Tooltip, Popconfirm, Empty } from "antd";
import React, { useContext } from "react";
import type { ColumnsType } from "antd/es/table";
import { IIndividualUser } from "../../domain/types";
import useUserViewPresenter from "@Totum/Groups/Presentation//hooks/useUserViewPresenter";
import { ActiveTabKey, Role, tagColors } from "@Totum/Groups/Constants";
import AssignmentMenu from "./GroupsAssignmentMenu";
import { SystemContext, UserContext } from "@Totum/Main/Constants";
import useCurrentGroupId from "@Totum/Groups/State/hooks/useCurrentGroupId";
import RoleTag from "./RoleTag";
import { CloseCircleFilled } from "@ant-design/icons";
import { Circle, PeopleAltTwoTone } from "@mui/icons-material";
import { MainContext } from "@Totum/Main/Context/provider";

interface IProps {
    activeTabKey: string;
    searchTerm?: string;
    currentPage: number;
    handlePageChange: (page: number) => void;
    isRootGroupView: boolean;
}

const UsersTable: React.FC<IProps> = ({ searchTerm, activeTabKey, currentPage, handlePageChange, isRootGroupView }) => {
    const { uiModel, handlers } = useUserViewPresenter(activeTabKey, currentPage, searchTerm, isRootGroupView);
    const [currentGroupId] = useCurrentGroupId();
    const { ui: uiState } = useContext(MainContext);

    const userColumns: ColumnsType<IIndividualUser> = [
        {
            dataIndex: "fullName",
            key: "fullName",
        },

        {
            dataIndex: "userId",
            key: "roles",
            render: (userId) => {
                const result = Object.entries(uiModel.roleTags ?? {})
                    .filter(([key, value]) => value.userId === userId)
                    .map(([key, value]) => ({ key, ...value }));

                return (
                    <div className="role-column">
                        {result.map((tag) => {
                            const color = tagColors[tag.label as Role] || "#D9D9D9"; // Default color for other custom roles
                            return <RoleTag tag={tag} color={color} />;
                        })}
                    </div>
                );
            },
        },
        {
            dataIndex: "status",
            key: "status",
            render: (status) => {
                const color = status === "invited" ? "#DFE0E1" : "#FC5555";
                return (
                    <>
                        {status === "active" ? null : (
                            <div
                                style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "5px" }}
                            >
                                <Circle style={{ color: color, fontSize: "13px" }} />
                                <div style={{ fontSize: "13px" }}>{status}</div>
                            </div>
                        )}
                    </>
                );
            },
        },
        {
            dataIndex: "userId",
            key: "assignment-menu",
            render: (userId) => {
                return (
                    <div>
                        {uiModel.canAddToGroups ? (
                            <AssignmentMenu
                                roleAssignments={
                                    activeTabKey === ActiveTabKey.Groups
                                        ? uiModel.roleAssignments?.filter((x) => x.userId === userId)
                                        : uiModel.roleAssignments?.filter(
                                              (x) => x.contextId === SystemContext && x.userId === userId
                                          )
                                }
                                creditAssignments={uiModel.creditAssignments?.filter(
                                    (x) => x.contextId === UserContext && x.instanceId === userId
                                )}
                                context={UserContext}
                                groupId={currentGroupId}
                                page={0}
                                userId={userId}
                                addAction={
                                    activeTabKey === ActiveTabKey.Groups
                                        ? uiModel.groupUserAddAction
                                        : uiModel.userSearchAddAction
                                }
                                systemRolesOnly={activeTabKey === ActiveTabKey.Users}
                            />
                        ) : null}
                    </div>
                );
            },
        },
        {
            dataIndex: "userId",
            key: "remove-user",
            render: (userId) => {
                return (
                    <>
                        {uiModel.allowRemove &&
                        !!handlers.handleRemoveGroupMember &&
                        activeTabKey === ActiveTabKey.Groups ? (
                            <Tooltip title="Remove from group">
                                <Popconfirm
                                    title={"Remove user from group and all child groups?"}
                                    onConfirm={() =>
                                        !!handlers.handleRemoveGroupMember && handlers.handleRemoveGroupMember([userId])
                                    }
                                    okText="Yes"
                                    cancelText="No"
                                    cancelButtonProps={{ className: "secondary" }}
                                >
                                    <button className={"no-style"}>
                                        <CloseCircleFilled />
                                    </button>
                                </Popconfirm>
                            </Tooltip>
                        ) : null}
                    </>
                );
            },
        },
    ];

    return (
        <>
            {searchTerm || uiModel.groupUsers || activeTabKey === ActiveTabKey.Users ? (
                <>
                    <div
                        style={activeTabKey === ActiveTabKey.Users ? { height: "750px" } : undefined}
                        id="table-container"
                    >
                        <Table
                            className="users-table"
                            showHeader={false}
                            pagination={false}
                            columns={userColumns}
                            loading={
                                activeTabKey === ActiveTabKey.Users
                                    ? uiModel.isFetchingAllUsers
                                    : uiModel.isFetchingGroupUsers
                            }
                            locale={{
                                emptyText: (
                                    <Empty
                                        className="ant-empty-custom-icon"
                                        description="No users found"
                                        image={<PeopleAltTwoTone className="large-icon" />}
                                    />
                                ),
                            }}
                            rowClassName={(record) =>
                                record.userId === uiState.currentUser?.instance ? "highlighted-row" : ""
                            }
                            dataSource={
                                activeTabKey === ActiveTabKey.Users
                                    ? uiModel.allUsers?.users
                                    : uiModel.groupUsers?.users
                            }
                            onRow={(record) => ({
                                onClick: () => {
                                    handlers.handleUserSelect(record.userId);
                                },
                            })}
                        />
                        {(uiModel.groupUsers?.totalItems ?? 0) > (uiModel.pageSize ?? 0) ||
                        (uiModel.allUsers?.totalItems ?? 0) > (uiModel.pageSize ?? 0) ? (
                            <Pagination
                                pageSize={
                                    activeTabKey === ActiveTabKey.Users
                                        ? uiModel.allUsers?.pageSize ?? 0
                                        : uiModel.groupUsers?.pageSize ?? 0
                                }
                                total={
                                    activeTabKey === ActiveTabKey.Users
                                        ? uiModel.allUsers?.totalItems ?? 0
                                        : uiModel.groupUsers?.totalItems ?? 0
                                }
                                showSizeChanger={false}
                                onChange={(page) => {
                                    handlePageChange(page);
                                }}
                                className="user-table-pagination"
                                current={currentPage + 1}
                            />
                        ) : null}
                    </div>
                </>
            ) : null}
        </>
    );
};

export default UsersTable;
